import { useState } from 'react';
import { closeModal, openToast } from 'utils/localDispatcher';
import { createChatStoreOrder } from 'apis/CommerceGraphql/chatOrder';
import type { GetChatOrderEligibilityQuery } from '__generated__/commerce';
import { numberInputComma } from 'utils/snippet';
import { CloseIcon, ILowercaseSerifCircleIcon } from 'utils/SVGIcons';
import { ChatOrderProductCandidatesModalStyle } from './ChatOrderProductCandidatesModalStyle';
import useChatOrderSelection from './hooks/useChatOrderSelection';
import { buildOrderMutationInput } from './logic/buildOrderMutationInput';
import ProductCard from './ProductCard';

export type ChatOrderPost = NonNullable<
  GetChatOrderEligibilityQuery['store']
>['mostRecentChatOrderPosts'][number];

export type ChatOrderProduct = ChatOrderPost['products'][number];

interface ChatOrderProductCandidatesModalProps {
  buyerAccountId: string;
  storeId: string;
  channelId: string;
  posts: ChatOrderPost[];
  onDirectInput: () => void;
}

const ChatOrderProductCandidatesModal = ({
  buyerAccountId,
  storeId,
  channelId,
  posts,
  onDirectInput,
}: ChatOrderProductCandidatesModalProps) => {
  const [selectedPostId, setSelectedPostId] = useState(() => posts[0]?.businessPostId ?? '');
  const selectedPost = posts.find((p) => p.businessPostId === selectedPostId) ?? posts[0];
  const products = selectedPost?.products ?? [];

  const {
    state,
    totalPrice,
    deliveryFeeCount,
    reset,
    toggleItem,
    increaseQuantity,
    decreaseQuantity,
    toggleDeliveryFee,
    selectDeliveryFeeOption,
    isItemSelected,
    getItemQuantity,
    getProductDeliveryState,
    isProductSelected,
    isSubmitDisabled,
  } = useChatOrderSelection(products);

  const handlePostChange = (postId: string) => {
    if (postId === selectedPostId) return;
    setSelectedPostId(postId);
    reset();
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeliveryFeeConfirm, setShowDeliveryFeeConfirm] = useState(false);

  const formatPrice = (amount: number) => `${numberInputComma(amount)}원`;

  const submitOrder = async () => {
    if (isSubmitDisabled || isSubmitting) return;

    setIsSubmitting(true);

    const productInputs = buildOrderMutationInput(state, products);

    try {
      const result = await createChatStoreOrder({
        buyerAccountId,
        storeId,
        products: productInputs,
        metadata: { chat: { channelId } },
      });

      if (result.error) {
        setIsSubmitting(false);
        switch (result.error.__typename) {
          case 'ChatOrderInvalidOrder':
            alert('결제 요청에 실패했어요. 입력을 다시 확인해주세요.');
            return;
          case 'ChatOrderStoreNotFound':
            alert('결제 요청에 실패했어요. 가게를 찾을 수 없어요.');
            return;
          case 'ChatOrderStoreNotOwned':
            alert('결제 요청에 실패했어요. 가게 소유권이 없어요.');
            return;
          default:
            alert('결제 요청에 실패했어요. 잠시 후 다시 시도해주세요.');
            return;
        }
      }

      openToast('결제 요청이 전송되었어요.');
      closeModal();
    } catch {
      setIsSubmitting(false);
      alert('결제 요청에 실패했어요. 잠시 후 다시 시도해주세요.');
    }
  };

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    if (deliveryFeeCount >= 2) {
      setShowDeliveryFeeConfirm(true);
      return;
    }
    submitOrder();
  };

  return (
    <ChatOrderProductCandidatesModalStyle>
      <button type="button" className="close-button" onClick={closeModal}>
        <CloseIcon size={24} />
      </button>

      <div className="title">결제요청</div>

      <div className="info-banner">
        <div className="info-icon">
          <ILowercaseSerifCircleIcon />
        </div>
        <div className="info-text">
          상품을 선택하고 결제를 요청하면, 채팅방에 주문서가 생성돼요.
          목록에 없는 상품은 &apos;직접 입력하기&apos;로 추가할 수 있어요.
        </div>
      </div>

      <div className="product-list">
        {posts.length > 1 && (
          <div className="post-chip-group" role="radiogroup">
            {posts.map((post) => {
              const active = post.businessPostId === selectedPost?.businessPostId;
              return (
                <button
                  key={post.businessPostId}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`post-chip${active ? ' active' : ''}`}
                  onClick={() => handlePostChange(post.businessPostId)}
                >
                  {post.title}
                </button>
              );
            })}
          </div>
        )}
        {products.map((product) => (
          <ProductCard
            key={product._id}
            product={product}
            formatPrice={formatPrice}
            onToggleItem={toggleItem}
            onIncreaseQuantity={increaseQuantity}
            onDecreaseQuantity={decreaseQuantity}
            onToggleDeliveryFee={toggleDeliveryFee}
            onSelectDeliveryFeeOption={selectDeliveryFeeOption}
            isItemSelected={isItemSelected}
            getItemQuantity={getItemQuantity}
            deliveryState={getProductDeliveryState(product._id)}
            isProductSelected={isProductSelected(product._id)}
          />
        ))}
      </div>

      <div className="button-group">
        <button type="button" className="direct-input-button" onClick={onDirectInput}>
          직접 입력하기
        </button>
        <button
          type="button"
          className="submit-button"
          onClick={handleSubmit}
          disabled={isSubmitDisabled || isSubmitting}
        >
          {isSubmitting ? '요청 중...' : isSubmitDisabled ? '결제 요청하기' : `${formatPrice(totalPrice)} 결제 요청하기`}
        </button>
      </div>

      {showDeliveryFeeConfirm && (
        <div className="confirm-overlay" onClick={() => setShowDeliveryFeeConfirm(false)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-title">배송비를 여러 번 선택했어요</div>
            <div className="confirm-description">배송비가 맞게 선택되었는지 확인해 주세요</div>
            <div className="confirm-buttons">
              <button
                type="button"
                className="confirm-button primary"
                onClick={() => {
                  setShowDeliveryFeeConfirm(false);
                  submitOrder();
                }}
              >
                이대로 주문서 전달하기
              </button>
              <button
                type="button"
                className="confirm-button secondary"
                onClick={() => setShowDeliveryFeeConfirm(false)}
              >
                주문서 수정
              </button>
            </div>
          </div>
        </div>
      )}
    </ChatOrderProductCandidatesModalStyle>
  );
};

export default ChatOrderProductCandidatesModal;
